import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Package, GitBranch, Clock, Layers, Sparkles, Search } from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Card } from '@/components/ui/card';
import { EarlyAccessForm } from '@/components/EarlyAccessForm';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const WHAT_IT_DOES = [
  {
    icon: Package,
    title: 'Complete component catalog',
    detail:
      'Every component installed in your WebCenter Content environment — Oracle stock components, third-party components, custom components built by previous teams or vendors. Names, versions, install paths, install dates.',
  },
  {
    icon: GitBranch,
    title: 'Dependency mapping',
    detail:
      'Which components depend on which. Identifies cascading impact of component changes — critical for upgrade planning, modernization sequencing, and decommissioning decisions.',
  },
  {
    icon: Clock,
    title: 'Last-modified analysis',
    detail:
      'When was each component last touched? Surfaces abandoned components, dormant customizations, and components that haven\'t been updated through multiple WebCenter Content patch cycles.',
  },
  {
    icon: Layers,
    title: 'Configuration deltas',
    detail:
      'For each component, the configuration differences from Oracle defaults. Identifies the customization surface area — the layer modernization and upgrade work has to account for.',
  },
  {
    icon: Search,
    title: 'Custom code detection',
    detail:
      'Surfaces custom Java services, custom iDoc Script, custom resources, and custom database objects added by components. Maps each to the business function it implements.',
  },
  {
    icon: Sparkles,
    title: 'Modernization impact scoring',
    detail:
      'Per-component scoring on impact for modernization, upgrade, and migration planning. Identifies components that translate forward cleanly vs. components that require deliberate handling.',
  },
];

const HOW_IT_WORKS = [
  {
    step: '1',
    title: 'Request early access',
    body: 'Sign up below. Andrew personally emails you within 1 business day to confirm fit and walk you through the installation.',
  },
  {
    step: '2',
    title: 'Install via Component Manager',
    body: 'Standard Oracle WebCenter Content component install — 5 minutes via the Component Manager admin UI. Read-only data collection. No vendor cloud callouts.',
  },
  {
    step: '3',
    title: 'Run the inventory',
    body: 'A new "Component Inventory Pro" menu appears under WCC Admin Tools. Click to run. The inventory completes in under 5 minutes for typical environments.',
  },
  {
    step: '4',
    title: 'Export the report',
    body: 'A clean PDF report covering catalog, dependencies, last-modified, configuration deltas, and modernization impact. Yours to keep.',
  },
];

const FAQS = [
  {
    q: 'Is this a real Oracle-supported WebCenter Content component?',
    a: 'Yes. Component Inventory Pro is a native WCC component installed via Component Manager — the standard Oracle-blessed extension mechanism. It runs inside your WCC environment using documented WCC services.',
  },
  {
    q: 'What does it cost?',
    a: 'Free for the duration of the December 2026 decision window. Part of EZ Cloud\'s WebCenter modernization outreach. No license fee, no per-component cost, no expiry.',
  },
  {
    q: 'How is this different from the Modernization Inspector?',
    a: 'The Modernization Inspector covers six dimensions of your WCC environment broadly (workflows, security, storage, customizations, decisions). Component Inventory Pro is a focused deep-dive on the components layer specifically — catalog, dependencies, last-modified, configuration deltas. Most customers use both: the Inspector for the big-picture decision context, Inventory Pro for the migration sequencing detail.',
  },
  {
    q: 'What WCC versions does Component Inventory Pro support?',
    a: 'WebCenter Content 11g, 12c, and 14c. The component detects the installed version and adapts data collection accordingly.',
  },
  {
    q: 'Does the component send any data back to EZ Cloud?',
    a: 'No. The component runs entirely inside your WCC environment and produces a local PDF report. No phone-home, no telemetry, no callout to ezcloud.ai.',
  },
];

const WccComponentInventory = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="WCC Component Inventory Pro — Oracle WebCenter Content Component Audit | EZ Cloud"
        description="A free native Oracle WebCenter Content component that produces a complete inventory of every component installed in your WCC environment — catalog, dependencies, last-modified, configuration deltas, modernization impact scoring. Early access for selected customers."
        canonical="https://ezcloud.ai/resources/wcc-component-inventory"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Resources', url: 'https://ezcloud.ai/resources/wcc-component-inventory' },
          {
            name: 'WCC Component Inventory Pro',
            url: 'https://ezcloud.ai/resources/wcc-component-inventory',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      <main>
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <Package className="w-4 h-4" aria-hidden />
              Free WCC Component · Early Access
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              WCC Component Inventory Pro
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              Every component, every dependency, every customization in your WebCenter Content
              environment — catalogued, mapped, scored for modernization impact. One install. One
              report.
            </p>
            <p className="text-lg text-white/70 max-w-3xl">
              Installs via standard Component Manager. Runs locally inside your WCC. No vendor
              cloud callouts, no phone-home, no data leaves your environment.
            </p>
          </div>
        </section>

        <section className="py-12 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-3xl">
            <EarlyAccessForm
              tool="WCC Component Inventory Pro"
              source="inventory-top"
              eventName="wcc_inventory_early_access"
              buttonLabel="Request early access"
            />
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-6xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What Component Inventory Pro produces
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Six dimensions of component analysis
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The component layer of a WebCenter Content environment is where customizations live.
              For modernization, migration, and upgrade planning, knowing this layer in detail
              matters.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {WHAT_IT_DOES.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-accent" aria-hidden />
                  </div>
                  <h3 className="text-gray-900 font-semibold mb-2">{title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How it works
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              From early access to printable component inventory in under an hour
            </h2>
            <div className="space-y-5 mt-12">
              {HOW_IT_WORKS.map((s) => (
                <Card key={s.step} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center text-accent font-bold text-xl flex-shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{s.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers about Component Inventory Pro
            </h2>
            <div className="space-y-5">
              {FAQS.map((f) => (
                <Card key={f.q} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{f.q}</h3>
                  <p className="text-gray-700 leading-relaxed">{f.a}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
              Ready to inventory your WCC components?
            </h2>
            <p className="text-xl text-white/80 text-center mb-10">
              Request early access. {FOUNDER.name} will personally email you within 1 business day.
            </p>
            <EarlyAccessForm
              tool="WCC Component Inventory Pro"
              source="inventory-bottom"
              eventName="wcc_inventory_early_access"
              buttonLabel="Request early access"
            />
          </div>
        </section>
      </main>
    </div>
  );
};

export default WccComponentInventory;
